import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ScanBarcode, Boxes, ClipboardList, Users, Banknote, ArrowRight, X } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';

const STEPS = [
  { target: 'nav-pos', icon: ScanBarcode, title: 'Ring up a sale', text: 'Scan a barcode or type a product name, take cash or EFTPOS, print the receipt. Try a refund too — stock and takings update instantly.', cta: 'Open the POS', to: '/pos' },
  { target: 'nav-inventory-toggle', icon: Boxes, title: 'Stock that looks after itself', text: 'Products, stock counts, short-dated markdowns and Smart PO — which writes your supplier order from what actually sold.', cta: 'See Smart PO', to: '/smart-po' },
  { target: 'nav-customers-toggle', icon: ClipboardList, title: 'Customer orders & requests', text: 'Special orders, "ordered & waiting", customer requests and reminders — editable, tracked, and never on a sticky note.', cta: 'Open customer orders', to: '/customer-orders' },
  { target: 'nav-staff-toggle', icon: Users, title: 'Your team\'s staff room', text: 'Weekly roster, daily tasks, notice board, store chat and the sales reports — everything the team needs in one place.', cta: 'See the sales reports', to: '/reports' },
  { target: 'nav-till', icon: Banknote, title: 'Till, cash & GST', text: 'Open the till with a float, close it with a count, and let the NZ GST return (under Admin) fill itself in from your sales and invoices.', cta: 'Open the till', to: '/till' },
];

const doneKey = (user) => `trialTourDone:${user?.business_id || user?.id || 'x'}`;

// Five-step spotlight tour for trial users — runs once, targets the sidebar, falls back to a centred card on small screens.
export const TrialTour = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(-1);
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (user?.business_status !== 'trial' || user?.impersonated_by) return;
    if (localStorage.getItem(doneKey(user))) return;
    const t = setTimeout(() => setStep(0), 900);
    return () => clearTimeout(t);
  }, [user]);

  useLayoutEffect(() => {
    if (step < 0) return;
    const measure = () => {
      const el = document.querySelector(`[data-testid="${STEPS[step].target}"]`);
      const r = el?.getBoundingClientRect();
      setRect(r && r.width > 0 && window.innerWidth >= 1024 ? { top: r.top, left: r.left, width: r.width, height: r.height } : null);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [step]);

  if (step < 0 || step >= STEPS.length) return null;
  const s = STEPS[step];
  const Icon = s.icon;
  const finish = () => { localStorage.setItem(doneKey(user), new Date().toISOString()); setStep(-1); };
  const next = () => (step === STEPS.length - 1 ? finish() : setStep(step + 1));
  const go = () => { finish(); navigate(s.to); };

  const cardStyle = rect
    ? { top: Math.min(Math.max(16, rect.top - 24), window.innerHeight - 320), left: rect.left + rect.width + 20 }
    : { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };

  return (
    <div className="fixed inset-0 z-[80]" data-testid="trial-tour">
      <div className="absolute inset-0 bg-black/55" onClick={next} />
      {rect && (
        <div className="absolute rounded-xl ring-4 ring-[#E8B04B] shadow-[0_0_0_9999px_rgba(0,0,0,0.0)] pointer-events-none transition-all duration-300"
             style={{ top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12, background: 'rgba(255,255,255,0.08)' }} />
      )}
      <div className="absolute w-[340px] max-w-[calc(100vw-32px)] bg-[#F6F1E7] text-[#14281A] rounded-2xl shadow-2xl p-5 animate-in fade-in zoom-in-95 duration-200" style={cardStyle} data-testid={`trial-tour-step-${step + 1}`}>
        <button onClick={finish} className="absolute top-3 right-3 text-[#14281A]/50 hover:text-[#14281A]" aria-label="Skip tour" data-testid="trial-tour-skip"><X className="w-4 h-4" /></button>
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#0B2E13]/60">
          <span>Step {step + 1} of {STEPS.length}</span>
          {step === 0 && <span className="text-[#B8860B]">· your hour starts now</span>}
        </div>
        <div className="mt-3 w-10 h-10 rounded-xl bg-[#0B2E13] text-[#E8B04B] flex items-center justify-center"><Icon className="w-5 h-5" /></div>
        <h3 className="font-heading text-2xl font-bold uppercase leading-none mt-3">{s.title}</h3>
        <p className="mt-2 text-sm text-[#14281A]/75 leading-relaxed">{s.text}</p>
        <div className="mt-4 flex items-center gap-2">
          <Button onClick={go} className="rounded-full bg-[#0B2E13] hover:bg-[#14451E] text-[#F6F1E7] h-9" data-testid="trial-tour-go">{s.cta} <ArrowRight className="w-4 h-4 ml-1.5" /></Button>
          <Button variant="ghost" onClick={next} className="h-9 text-[#0B2E13]" data-testid="trial-tour-next">{step === STEPS.length - 1 ? 'Done' : 'Next'}</Button>
        </div>
        <div className="mt-4 flex gap-1.5">
          {STEPS.map((_, i) => <span key={i} className={`h-1.5 rounded-full transition-all ${i === step ? 'w-6 bg-[#E8B04B]' : 'w-1.5 bg-[#0B2E13]/20'}`} />)}
        </div>
      </div>
    </div>
  );
};
